import { useCallback } from 'react';
import { getTrimPaymentApiPath, getTrimPaymentRequestObject } from '../../apiHooks/utils';
import { primaryCalculatorId, selectPaymentOptions as selectPaymentOptionsFromCalculator, selectPrimaryCalculatorLoyaltyOfferKey, selectPrimaryCalculatorPaymentOptions, } from '../reducers/financialCalculators';
import { selectCurrentVehicle } from '../reducers/currentVehicle';
import { selectInteriorColorIsInvalid } from '../reducers/ui';
import { useApiHost, useAppName, useLanguage, usePageState, useSettings, useUserOrDefaultProvince, } from '../../utils/sitecoreContext';
import { useAppSelector } from '../utils';
import { useGetPaymentQuery } from '../apis/hondaRestApi';
export const useCreatePaymentRequest = (vehicleType, provinceCode) => {
    const { defaultProvince } = useSettings();
    const provinceCodeFromGeoContext = useUserOrDefaultProvince();
    const languageCode = useLanguage();
    const pageState = usePageState();
    const hondaRestApiHost = useApiHost();
    const appName = useAppName();
    const apiPath = getTrimPaymentApiPath(appName, pageState, vehicleType);
    const url = `https://${hondaRestApiHost}/${apiPath}`;
    return useCallback(({ vehicle, paymentOptions }) => ({
        url,
        languageCode,
        payload: getTrimPaymentRequestObject({
            appName,
            defaultProvince,
            ...paymentOptions,
            overriddenIsSellingProvince: !!paymentOptions.overriddenIsSellingProvince,
            provinceCode: provinceCode !== null && provinceCode !== void 0 ? provinceCode : provinceCodeFromGeoContext,
            payload: vehicle,
        }),
    }), [appName, defaultProvince, provinceCode, provinceCodeFromGeoContext, url, languageCode]);
};
/**
 * Data fetching hook for payment through RTK query
 * @example
 * // overrides province code from geo context
 * useFetchPayment(vehicle, paymentOptions, '', true, 'AB')
 */
export const useFetchPayment = (vehicle, paymentOptions, vehicleType, shouldFetch = true, provinceCode) => {
    const interiorColorIsInvalid = useAppSelector(selectInteriorColorIsInvalid);
    const isVehicleValid = !!vehicle.modelYear &&
        !!vehicle.modelKey &&
        !!vehicle.trimKey &&
        !!vehicle.transmissionKey &&
        !!vehicle.exteriorColorKey;
    const createPaymentRequest = useCreatePaymentRequest(vehicleType, provinceCode);
    const { url, payload, languageCode } = createPaymentRequest({ vehicle, paymentOptions });
    const { data, isFetching, isLoading, isSuccess, error, isUninitialized } = useGetPaymentQuery({
        url,
        body: payload,
        languageCode,
    }, { skip: !shouldFetch || !isVehicleValid || !payload || !url });
    return {
        data: data === null || data === void 0 ? void 0 : data.transformedData,
        originalResponse: data === null || data === void 0 ? void 0 : data.originalResponse,
        isFetching,
        isLoading,
        isSuccess,
        error: error || interiorColorIsInvalid,
        isUninitialized,
        payloadBody: payload,
    };
};
export const useFetchCurrentVehiclePayment = (vehicleType, shouldFetch = true) => {
    const { trimKey, transmissionKey, exteriorColorKey, interiorColorKey, accessoryKeys, protectionKeys, protectionAddOnKeys, modelKey, modelYear, } = useAppSelector(selectCurrentVehicle);
    const paymentOptions = useAppSelector(selectPrimaryCalculatorPaymentOptions);
    const primaryCalculatorLoyaltyOfferKey = useAppSelector(selectPrimaryCalculatorLoyaltyOfferKey);
    paymentOptions.loyaltyOfferKey = primaryCalculatorLoyaltyOfferKey || paymentOptions.loyaltyOfferKey;
    return useFetchPayment({
        trimKey,
        modelKey,
        modelYear,
        transmissionKey,
        exteriorColorKey,
        interiorColorKey,
        accessories: Array.isArray(accessoryKeys) ? accessoryKeys : [],
        protections: Array.isArray(protectionKeys) ? protectionKeys : [],
        protectionAddOns: Array.isArray(protectionAddOnKeys) ? protectionAddOnKeys : [],
    }, paymentOptions, vehicleType, shouldFetch);
};
export const useFetchCurrentVehicleFinancialCalculatorPayment = (calculatorId, shouldSkip, accessoryKeys) => {
    const { trimKey, transmissionKey, exteriorColorKey, interiorColorKey, accessoryKeys: defaultAccessoryKeys, protectionKeys, protectionAddOnKeys, modelKey, modelYear, } = useAppSelector(selectCurrentVehicle);
    const paymentOptions = useAppSelector(state => selectPaymentOptionsFromCalculator(state, calculatorId));
    const isPrimaryCalculator = calculatorId === primaryCalculatorId;
    const primaryCalculatorLoyaltyOfferKey = useAppSelector(selectPrimaryCalculatorLoyaltyOfferKey);
    paymentOptions.loyaltyOfferKey =
        (isPrimaryCalculator && primaryCalculatorLoyaltyOfferKey) || paymentOptions.loyaltyOfferKey;
    const accessories = Array.isArray(accessoryKeys) ? accessoryKeys : defaultAccessoryKeys;
    return useFetchPayment({
        trimKey,
        modelKey,
        modelYear,
        transmissionKey,
        exteriorColorKey,
        interiorColorKey,
        accessories: Array.isArray(accessories) ? accessories : [],
        protections: Array.isArray(protectionKeys) ? protectionKeys : [],
        protectionAddOns: Array.isArray(protectionAddOnKeys) ? protectionAddOnKeys : [],
    }, paymentOptions, '', !shouldSkip);
};
export default useFetchPayment;
